import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@xing-com/button';
import type {
  BoundedTotal,
  JobItemResult,
  JobSearchQuery,
} from '@xing-com/crate-common-graphql-types';
import {
  LazyLoading,
  LAZY_LOADING_TYPE_BUTTON,
} from '@xing-com/crate-entity-pages-common';
import { getTrackingUrl } from '@xing-com/crate-entity-pages-common/src/tracking';
import {
  JobsBookmark,
  JobsListItem,
  JobsEmptyState,
  checkBookmarkState,
} from '@xing-com/crate-entity-pages-jobs';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { BodyCopy, Meta } from '@xing-com/typography';

import * as Styled from './jobs-detail.styles';

const XING_JOBBOX_URL = 'https://www.xing.com/jobs/jobbox';

type JobHeaderProps = {
  boundedTotal?: BoundedTotal | null;
  showJobbox: boolean;
};

const JobHeader: FC<JobHeaderProps> = ({ boundedTotal, showJobbox }) => {
  const { total } = boundedTotal || { total: 0 };

  return (
    <Styled.Header>
      <Meta noMargin size={'small'}>
        <FormattedMessage
          id={total ? 'EP_JOB_ADS_FOUND' : 'EP_SOME_JOB_ADS_FOUND'}
          values={{ total: total > 99 ? '99+' : total }}
        />
      </Meta>
      {showJobbox ? (
        <Styled.JobBoxLink>
          <JobsBookmark initialBookmark fixedValue />
          <TextButton
            href={getTrackingUrl({
              to: XING_JOBBOX_URL,
              trackingKey: 'viewJobOffers',
            })}
            // @ts-expect-error FIXME: SC6
            target="_blank"
          >
            <BodyCopy
              fontWeight="bold"
              lineHeight="dense"
              size={'small'}
              noMargin
            >
              <FormattedMessage id="EP_JOB_JOBBOX" />
            </BodyCopy>
          </TextButton>
        </Styled.JobBoxLink>
      ) : null}
    </Styled.Header>
  );
};

type JobDetailTypeProps = {
  boundedTotal?: BoundedTotal | null;
  searchQuery?: Partial<JobSearchQuery>;
  jobResults?: Partial<JobItemResult>[];
  onLazyLoad: () => void;
  paginationLoading: boolean;
};

export const JobsDetailSkeleton: FC = () => {
  return (
    <Styled.Wrapper>
      <JobsListItem.Skeleton />
      <JobsListItem.Skeleton />
    </Styled.Wrapper>
  );
};

export const JobsDetail: FC<JobDetailTypeProps> = ({
  boundedTotal,
  searchQuery,
  jobResults,
  onLazyLoad,
  paginationLoading,
}) => {
  const { isLoggedIn } = useLoginState();

  const showDescription = !!searchQuery?.body?.keywords;

  const isPaginationEnabled = () => {
    const isLimitReached =
      boundedTotal?.bound !== null &&
      Number(jobResults?.length) >= Number(boundedTotal?.bound);
    if (isLimitReached) {
      return true;
    }
    if (boundedTotal?.total === jobResults?.length) return false;
    return true;
  };

  if (!jobResults?.length) {
    return (
      <Styled.EmptyStateContainer>
        <JobsEmptyState statusCode={isLoggedIn ? undefined : 404} />
      </Styled.EmptyStateContainer>
    );
  }

  return (
    <Styled.Wrapper>
      {<JobHeader boundedTotal={boundedTotal} showJobbox={isLoggedIn} />}
      {jobResults
        ?.filter(({ jobDetail }) => !!jobDetail?.id)
        .map(({ jobDetail: jobQuery, descriptionHighlight }) => {
          const jobDetail =
            jobQuery?.__typename === 'VisibleJob' ? jobQuery : null;

          const cities =
            jobDetail?.locations
              ?.map((location) => location?.city)
              .filter(Boolean) || [];

          return (
            <JobsListItem
              activeUntil={jobDetail?.activeUntil}
              key={jobDetail?.id}
              paid={jobDetail?.paid}
              // @ts-expect-error TODO: fix this type
              title={jobDetail?.title}
              // @ts-expect-error TODO: fix this type
              companyName={jobDetail?.companyInfo?.company?.companyName}
              city={cities}
              // @ts-expect-error TODO: fix this type
              refreshedAt={jobDetail?.refreshedAt}
              url={getTrackingUrl({
                to: jobDetail?.url ?? '',
                trackingKey: 'viewJobProfile',
              })}
              // @ts-expect-error TODO: fix this type
              id={jobDetail?.id}
              bookmarkState={checkBookmarkState(
                jobDetail?.userInteractions?.bookmark?.state
              )}
              showBookmark={isLoggedIn}
              // @ts-expect-error TODO: fix this type
              descriptionHighlight={descriptionHighlight}
              showDescription={showDescription}
            />
          );
        })}
      <Styled.Pagination>
        <LazyLoading
          type={LAZY_LOADING_TYPE_BUTTON}
          isLoading={paginationLoading}
          enabled={isPaginationEnabled()}
          onLazyLoad={onLazyLoad}
        />
      </Styled.Pagination>
    </Styled.Wrapper>
  );
};
